import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import moment from 'moment';
import { Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { OrderBundle, OrderParkingVM } from '../../../../../models/orderModel';
import { AvailableParking, OrderBooking, ParkingVM } from '../../../../../models/parkingsite.model';
import { ParkingService } from '../../../../../services/parking.service';
import { ParkingEditConfirmComponent } from './parking-edit-confirm/parking-edit-confirm.component';
import { BackOfficeService } from '../../../../../services/backoffice.service';
import { MyConfigService } from '../../../../../services/my-config.service';
import { ConfirmDeleteComponent } from '../../../users/edit-user/confirm-delete/confirm-delete.component';
import { DatePipe } from '@angular/common';
import { log } from 'console';

@Component({
  selector: 'app-parking-edit',
  templateUrl: './parking-edit.component.html',
  styleUrls: ['./parking-edit.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ParkingEditComponent implements OnInit, OnDestroy {
  availableParkingSpots: AvailableParking[];
  allAvailableParkingSpots: AvailableParking[];
  filteredAvailableParkingSpots: Observable<AvailableParking[]>;
  filteredAllAvailableParkingSpots: Observable<AvailableParking[]>;
  editParkingForm: UntypedFormGroup;
  editAdminParkingForm: UntypedFormGroup;
  orderBundle: OrderBundle;
  orderParkingId: string;
  parkingSiteName: string;
  componentDestroyed$: Subject<boolean> = new Subject();
  @Output() saveChangesEvent = new EventEmitter<any>();
  loading: boolean = false;
  activeRequests: number = 0;
  isAdmin: boolean = false;
  parkingFromTime: string;
  parkingToTime: string;
  checkInTime: string;
  checkOutTime: string;
  editOrderParkingForm: UntypedFormGroup;
  orderParkingVM: OrderParkingVM;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: OrderBooking,
    private fb: UntypedFormBuilder,
    private parkingService: ParkingService,
    private backOfficeService: BackOfficeService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private router: Router,
    private myConfigService: MyConfigService,
    private dialogRef: MatDialogRef<ParkingEditComponent>,
    public datepipe: DatePipe
  ) {
    this.initEditParkingForm('');
  }

  ngOnInit(): void {
    this.checkIfAdmin();
    this.getOrderBundle();
    this.getAvailableParkingSites();
    this.getAllAvailableParkingSites();

  }
  checkIfAdmin() {
    if (this.myConfigService.hasOrganizationIdRole(this.data.order.organizationId, 'ParkingOperator', this.data.order.organizationId) ||
      this.myConfigService.hasOrganizationIdRole(this.data.order.organizationId, 'ParkingAdmin', this.data.order.organizationId)) {
      this.isAdmin = true;
    }
  }

  setLoadingState(state: boolean) {
    this.loading = state;
  }

  incrementRequests() {
    this.activeRequests++;
    this.setLoadingState(true);
  }

  decrementRequests() {
    this.activeRequests--;
    if (this.activeRequests === 0) {
      this.setLoadingState(false);
    }
  }

  initEditParkingForm(value: string) {
    this.editParkingForm = this.fb.group({
      parkingSpot: [{ value: value, disabled: this.loading }]
    });
    this.editAdminParkingForm = this.fb.group({
      parkingSpot: [{ value: value, disabled: this.loading }]
    });
  }

  displayFn(parkingSpot: AvailableParking): string {
    return parkingSpot && parkingSpot.parkingSiteName ? parkingSpot.parkingSiteName : '';
  }

  private _filter(parkingSiteName: string, parkingSpots: AvailableParking[]): AvailableParking[] {
    const filterValue = parkingSiteName.toLowerCase();

    return parkingSpots.filter(parkingSpot => parkingSpot.parkingSiteName.toLowerCase().includes(filterValue));
  }

  getOrderBundle() {
    this.incrementRequests();
    this.backOfficeService.getOrderBundle(this.data.order.orderId).subscribe(orderBundle => {
      this.decrementRequests();
      this.orderBundle = orderBundle[0];
      this.getParkingSiteId(this.orderBundle);
    });
    this.parkingSiteName = this.data.parkingSiteName;
    this.orderParkingId = this.data.booking.orderParkingId;
  }

  selectedParkingSpot(selectedParkingSpotId: string) {
    const dialogRef = this.dialog.open(ParkingEditConfirmComponent, {
      data: { parkingSiteId: selectedParkingSpotId, orderParkingId: this.orderParkingId, admin: false }
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.snackBar.open('Dina ändringar är sparade...', 'OK', {
            duration: 3000,
            verticalPosition: 'top'
          });
          this.saveChangesEvent.emit(true);
          this.dialogRef.close();
        }
      });
  }

  selectedAdminParkingSpot(selectedParkingSpotId: string) {
    const dialogRef = this.dialog.open(ParkingEditConfirmComponent, {
      data: { parkingSiteId: selectedParkingSpotId, orderParkingId: this.orderParkingId, admin: true }
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.snackBar.open('Dina ändringar är sparade...', 'OK', {
            duration: 3000,
            verticalPosition: 'top'
          });
          this.saveChangesEvent.emit(true);
          this.dialogRef.close();
        }
      });
  }

  getParkingSiteId(orderBundle: OrderBundle) {
    orderBundle.orderBundleRows.forEach(bundleRow => {
      if (bundleRow.orderParkingVM) {
        this.orderParkingId = bundleRow.orderParkingVM.orderParkingId;
        this.parkingSiteName = bundleRow.orderParkingVM.parkingSiteName;
      }
    });
  }

  getAvailableParkingSites() {
    this.incrementRequests();
    const productId = this.data.booking.productId;
    const fromDate = moment(this.data.booking.bookedFrom).format();
    const toDate = moment(this.data.booking.bookedTo).format();
    this.parkingService
      .getAvailableParkingSites(this.data.parkingId, productId, fromDate, toDate)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(parkingSites => {
        this.decrementRequests();

        parkingSites = this.sortParkingSpotList(parkingSites);

        const selectedParkingSpot = parkingSites.find(parkingSite => parkingSite.parkingSiteName === this.parkingSiteName);
        if (selectedParkingSpot) this.editParkingForm.controls.parkingSpot.patchValue(selectedParkingSpot);

        this.availableParkingSpots = parkingSites;
        this.filteredAvailableParkingSpots = this.editParkingForm.controls.parkingSpot.valueChanges.pipe(
          startWith(''),
          map(value => {
            const parkingSiteName = typeof value === 'string' ? value : value?.parkingSiteName;
            return parkingSiteName ? this._filter(parkingSiteName as string, this.availableParkingSpots) : this.availableParkingSpots.slice();
          }),
        );
      });
  }

  getAllAvailableParkingSites() {
    if (this.isAdmin) {
      this.incrementRequests();
      const productId = this.data.booking.productId;
      const fromDate = moment(this.data.booking.bookedFrom).format();
      const toDate = moment(this.data.booking.bookedTo).format();
      this.parkingService
        .getAllAvailableParkingSites(this.data.parkingId, productId, fromDate, toDate)
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe(parkingSites => {
          this.decrementRequests();

          parkingSites = this.sortParkingSpotList(parkingSites);

          const selectedParkingSpot = parkingSites.find(parkingSite => parkingSite.parkingSiteName === this.parkingSiteName);
          if (selectedParkingSpot) this.editAdminParkingForm.controls.parkingSpot.patchValue(selectedParkingSpot);

          this.allAvailableParkingSpots = parkingSites;
          this.filteredAllAvailableParkingSpots = this.editAdminParkingForm.controls.parkingSpot.valueChanges.pipe(
            startWith(''),
            map(value => {
              const parkingSiteName = typeof value === 'string' ? value : value?.parkingSiteName;
              return parkingSiteName ? this._filter(parkingSiteName as string, this.allAvailableParkingSpots) : this.allAvailableParkingSpots.slice();
            }),
          );
        });
    }
  }

  setPaid() {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Vill du sätta status Paid på ${this.data?.booking?.carId}?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.incrementRequests();
        this.parkingService
          .setStatusPaid(this.orderParkingId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe(orderParking => {
            if (orderParking?.status?.toLowerCase() === 'paid') {
              this.snackBar.open('Statusen är ändrad till Paid', 'OK', {
                verticalPosition: 'top',
                duration: 1000
              });
            } else {
              this.snackBar.open('Något gick fel', 'OK', {
                verticalPosition: 'top'
              });
            }
            this.saveChangesEvent.emit();
            this.decrementRequests();
            this.dialogRef.close();
          },
            error => {
              this.decrementRequests();
              this.snackBar.open(`Felkod: ${error.status}`, 'Doh!', {
                verticalPosition: 'top'
              });
            });
      }
    });
  }

  setReserved() {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Vill du sätta status Reserved på ${this.data?.booking?.carId}?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.incrementRequests();
        this.parkingService
          .setStatusReserved(this.orderParkingId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe(orderParking => {
            if (orderParking?.status?.toLowerCase() === 'reserved') {
              this.snackBar.open('Statusen är ändrad till Reserved', 'OK', {
                verticalPosition: 'top',
                duration: 1000
              });
            } else {
              this.snackBar.open('Något gick fel', 'OK', {
                verticalPosition: 'top'
              });
            }
            this.saveChangesEvent.emit();
            this.decrementRequests();
            this.dialogRef.close();
          },
            error => {
              this.decrementRequests();
              this.snackBar.open(`Felkod: ${error.status}`, 'Doh!', {
                verticalPosition: 'top'
              });
            });
      }
    });
  }

  sendOrderParkingCheckinConfirmation(orderId: string) {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Är du säker på att du vill skicka parkeringansvisning?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.incrementRequests();
        this.backOfficeService
          .sendOrderParkingCheckinConfirmation(orderId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe({
            next: status => {
              this.snackBar.open(`Du har skickat parkeringansvisnings mail till: ${this.data.email}`, 'OK', {
                duration: 2000,
                verticalPosition: 'top'
              });
            },
            complete: () => {
              this.decrementRequests();
            }
          });
      }
    });
  }

  sortParkingSpotList(parkingList: AvailableParking[]): AvailableParking[] {
    const sortedList = [...parkingList];

    sortedList.sort((a, b) => {
      const processParkingName = (name: string) => {
        const lastColonIndex = name.lastIndexOf(':');

        if (lastColonIndex === -1) {
          return [name.replace(/([a-zA-Z]+)/g, ''), '0'];
        }

        const firstPart = name.substring(0, lastColonIndex).replace(/([a-zA-Z]+)/g, '');
        const secondPart = name.substring(lastColonIndex + 1);
        return [firstPart, secondPart];
      };

      const parkingSiteNameA = processParkingName(a.parkingSiteName);
      const parkingSiteNameB = processParkingName(b.parkingSiteName);

      const firstPartA = +parkingSiteNameA[0];
      const firstPartB = +parkingSiteNameB[0];

      if (firstPartA < firstPartB) return -1;
      if (firstPartA > firstPartB) return 1;

      const secondPartA = +parkingSiteNameA[1] || 0;
      const secondPartB = +parkingSiteNameB[1] || 0;

      if (secondPartA < secondPartB) return -1;
      if (secondPartA > secondPartB) return 1;

      return 0;
    });

    return sortedList;
  }

  close() {
    this.dialogRef.close();
  }

  save() {
    if (this.editParkingForm.dirty && this.editParkingForm.value.parkingSpot && this.editParkingForm.value.parkingSpot !== '' && this.editParkingForm.value.parkingSpot.parkingSiteId) {
      this.selectedParkingSpot(this.editParkingForm.value.parkingSpot.parkingSiteId);
    } else if (this.editAdminParkingForm.dirty && this.editAdminParkingForm.value.parkingSpot && this.editAdminParkingForm.value.parkingSpot !== '' && this.editAdminParkingForm.value.parkingSpot.parkingSiteId) {
      this.selectedAdminParkingSpot(this.editAdminParkingForm.value.parkingSpot.parkingSiteId);
    } else {
      this.snackBar.open('Vänligen välj en parkering', 'OK', {
        duration: 3000,
        verticalPosition: 'top'
      });
    }
  }

  goToOrder(): void {
    const url = this.router.serializeUrl(this.router.createUrlTree([`/admin/back-office24-order/${this.data.order.orderId}`]));
    window.open(url, '_blank');
  }

  ngOnDestroy() {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  getBookingStatusText(status: number): string {
    switch (status) {
      case 1:
        return 'New';
      case 2:
        return 'Paid';
      case 3:
        return 'Reserved';
      case 4:
        return 'Checkin';
      case 8:
        return 'Checkout';
      case 16:
        return 'NoShow';
      case 64:
        return 'Cancelled';
      default:
        return 'Unknown';
    }
  }
}
